
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import {mapActions, mapMutations} from "vuex";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import ContractProfileForm from "@/components/organisms/ContractProfileTemplatePanel/ContractProfileForm.vue";
import {defineComponent, PropType} from "vue";
import {ContractProfile} from "@/store/modules/hcs/contractProfile/model";

interface ViewContractProfile extends ContractProfile {
  selected?: boolean;
}

export default defineComponent({
  name: "GenericContractProfileTab",
  components: {
    ContractProfileForm,
    TextFieldLike, IconBtn, IconBtns, ReadonlyCheckbox, TableInline, SimpleDialog, TabItemTemplate},
  props: {
    contractProfileTemplateId: {
      type: [String, null] as PropType<string | null>,
      required: false,
      default: null,
    },
    tradeContractId: {
      type: [String, null] as PropType<string | null>,
      required: false,
      default: null,
    },
    tradeContractFormatId: {
      type: [String, null] as PropType<string | null>,
      required: false,
      default: null,
    },
    contractProfiles: {
      type: Array as PropType<ContractProfile[]>,
      required: true
    },
    notationType: {
      type: String,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false
    },
    hideSelection: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
  },
  emits: [
      'change:contract-profiles'
  ],
  data() {
    return {
      createDialog: {
        visible: false,
        form: {title: '', content: ''} as ContractProfile,
      },
      deleteDialog: {
        visible: false,
        form: {title: '', content: ''} as ContractProfile,
      },
      editDialog: {
        visible: false,
        form: {title: '', content: ''} as ContractProfile,
      },
      indeterminateSelected: [] as string[], // 選択ロード中
    };
  },
  computed: {
    notationTypeTitle() {
      switch (this.notationType) {
        case 'clause':
          return '契約条項';
        case 'parameter':
          return '契約事項';
        default:
          throw new Error(`unknown notationType: ${this.notationType}`);
      }
    },
    contractProfileView(): ViewContractProfile[] {
      return this.contractProfiles
          .filter(cp => cp.notationType === this.notationType);
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      createContractProfile: 'hcs/contractProfile/create',
      updateContractProfile: 'hcs/contractProfile/update',
      deleteContractProfile: 'hcs/contractProfile/delete',
    }),
    async handleSelectedChange(row: ViewContractProfile, isSelected: boolean) {
      try {
        this.indeterminateSelected.push(row.id);
        await this.updateContractProfile({
          id: row.id,
          selected: isSelected
        });
        row.selected = isSelected;
      } catch(e) {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `選択の反映に失敗しました`,
        });
      } finally {
        const pos = this.indeterminateSelected.indexOf(row.id);
        if(pos !== -1) {
          this.indeterminateSelected.splice(pos, 1);
        }
      }
    },
    openCreateDialog() {
      this.createDialog = {
        visible: true,
        form: {title: '', content: ''} as ContractProfile
      }
    },
    closeCreateDialog(apply: boolean) {
      if(apply) {
        this.showSnackbar({
          message: `${this.notationTypeTitle}を作成中...`,
          timeout: false,
          loading: true
        });
        this.createContractProfile({
          notationType: this.notationType,
          title: this.createDialog.form.title,
          content: this.createDialog.form.content,
          contractProfileTemplateId: this.contractProfileTemplateId,
          tradeContractId: this.tradeContractId,
          tradeContractFormatId: this.tradeContractFormatId,
        }).then(newContactProfile => {
          this.showSnackbar({
            message: `${this.notationTypeTitle}を作成しました`,
          });
          this.contractProfiles.push(newContactProfile);
          this.$emit('change:contract-profiles');
        }, () => {
          this.showSnackbar({
            color: 'failed',
            message: `${this.notationTypeTitle}の作成に失敗しました`,
          });
        });
      }
      this.createDialog.visible = false;
    },
    openDeleteDialog(row: ViewContractProfile) {
      this.deleteDialog = {
        visible: true,
        form: row,
      }
    },
    closeDeleteDialog(apply: boolean) {
      if(apply) {
        this.showSnackbar({
          message: `${this.notationTypeTitle}を削除中...`,
          timeout: false,
          loading: true
        });
        this.deleteContractProfile(this.deleteDialog.form.id).then(contractProfileId => {
          this.showSnackbar({
            message: `${this.notationTypeTitle}を削除しました`,
          });
          const index = this.contractProfiles.findIndex(cp => cp.id === contractProfileId);
          if(index !== -1) {
            this.contractProfiles.splice(index, 1);
            this.$emit('change:contract-profiles');
          }
        }, () => {
          this.showSnackbar({
            color: 'failed',
            message: `${this.notationTypeTitle}の削除に失敗しました`,
          });
        });
      }
      this.deleteDialog.visible = false;
    },
    openEditDialog(row: ViewContractProfile) {
      this.editDialog = {
        visible: true,
        form: row,
      };
    },
    closeEditDialog(apply: boolean) {
      if(apply) {
        this.showSnackbar({
          message: `${this.notationTypeTitle}を更新中...`,
          timeout: false,
          loading: true
        });
        this.updateContractProfile(this.editDialog.form).then(contractProfile => {
          this.showSnackbar({
            message: `${this.notationTypeTitle}を更新しました`,
          });
          const index = this.contractProfiles.findIndex(cp => cp.id === contractProfile.id);
          if(index !== -1) {
            this.contractProfiles.splice(index, 1, contractProfile);
            this.$emit('change:contract-profiles');
          }
        }, () => {
          this.showSnackbar({
            color: 'failed',
            message: `${this.notationTypeTitle}の更新に失敗しました`,
          });
        });
      }
      this.editDialog.visible = false;
    }
  }
});
